import type { Edge } from '../../../core/models/scene';

/**
 * 2D point with readonly coordinates
 */
export interface Point2D {
  readonly x: number;
  readonly y: number;
}

/**
 * Dimensions with readonly width and height
 */
export interface Dimensions {
  readonly width: number;
  readonly height: number;
}

/**
 * Valid layout directions for org charts
 */
export const ORG_LAYOUT_DIRECTIONS = ['DOWN', 'UP', 'LEFT', 'RIGHT'] as const;
export type OrgLayoutDirection = (typeof ORG_LAYOUT_DIRECTIONS)[number];

/**
 * Org chart edge types - all connector types that represent org relationships
 */
export const ORG_EDGE_TYPES = ['reporting', 'dotted', 'matrix', 'team', 'occupies'] as const;
export type OrgEdgeType = (typeof ORG_EDGE_TYPES)[number];

/**
 * Type guard to check if an edge is an org chart edge
 */
export function isOrgEdge(edge: Edge): boolean {
  return ORG_EDGE_TYPES.includes(edge.type as OrgEdgeType);
}

/**
 * Anchor positions for edge connections
 */
export type AnchorPosition = 'top' | 'right' | 'bottom' | 'left';

/**
 * Edge anchor update record for undo/redo tracking
 */
export interface EdgeAnchorUpdate {
  readonly edgeId: string;
  readonly prevFromAnchor?: AnchorPosition;
  readonly prevToAnchor?: AnchorPosition;
  readonly prevFromOffset?: number;
  readonly prevToOffset?: number;
  readonly nextFromAnchor?: AnchorPosition;
  readonly nextToAnchor?: AnchorPosition;
}

/**
 * Edge route update record for undo/redo tracking
 */
export interface EdgeRouteUpdate {
  readonly edgeId: string;
  readonly prevLayoutPoints?: readonly number[];
  readonly prevLayoutSource?: string;
  readonly prevLayoutDirection?: string;
}
